/**
 * Split layout for the sign-in and password reset screens, modeled on Kanvo's
 * login: brand panel on the left at lg and up, form on the right. Kanvo uses a
 * photo for the panel. Cubby has no photography yet, so the panel draws a
 * cubby shelf in CSS instead, which also avoids shipping a large image on the
 * first screen anyone sees.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";

// One entry per cubby, left to right, top to bottom. null is an empty cubby.
const SHELF: (string | null)[] = [
  "bg-clay", "bg-sun", null, "bg-sky",
  "bg-sage", "bg-blush", "bg-sun", null,
  null, "bg-sky", "bg-clay", "bg-sage",
  "bg-blush", null, "bg-sage", "bg-sun",
];

function CubbyShelf() {
  return (
    <div
      aria-hidden="true"
      className="mx-auto grid w-full max-w-sm grid-cols-4 gap-3 rounded-3xl bg-leaf-900/60 p-4"
    >
      {SHELF.map((color, index) => (
        <div
          key={index}
          className="relative flex aspect-[3/4] items-end justify-center rounded-2xl bg-leaf-700/70 p-2"
        >
          <span className="absolute top-2 h-1.5 w-1.5 rounded-full bg-leaf-100/40" />
          {color ? <span className={`h-3/5 w-4/5 rounded-xl ${color}`} /> : null}
        </div>
      ))}
    </div>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen w-full bg-cream">
      <aside className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-leaf-800 p-12 lg:flex">
        <Link to="/signup" aria-label="About Cubby" className="self-start">
          <Logo tone="light" />
        </Link>

        <CubbyShelf />

        <div>
          <p className="font-display text-3xl font-medium leading-tight text-white">
            The school day, from drop-off to pickup.
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-leaf-100/80">
            Check-ins, classroom ratios, billing and family updates for preschools.
          </p>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden">
            <Logo />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
